import styled, { css } from 'styled-components'
import { black, darkGrey, darkOrange, divider, textMuted, warmTint, white } from '@/theme/colors'
import { fieldStyles } from '@/theme/recipes'
import { radii } from '@/theme/tokens'
import { text } from '@/theme/typography'
import { Button } from '@components/Button'
import { AddLinkButton } from '@pages/Itinerary/AddEventModal/FormComponents.styles'
import { BlankCell, CalendarCard, DayCell, rangeAccent } from '@pages/Trips/CreateTripFlow/CreateTripCalendar.styles'

/*
 * The create-event flow's steps, on the create-trip card: labelled field
 * groups, the day calendar, and the Extras sections. The controls are the
 * app's own — the field recipe, Button, the create-trip calendar's card and
 * cells.
 */

export const StepForm = styled.div`
	display: flex;
	flex-direction: column;
	gap: 32px;
`

/* ─── Field group: title (with its marker), hint, input ─── */

export const Group = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
`

const groupTitle = css`
	${text.title}
	color: ${black};
`

/** A group's title, labelling the one control under it. */
export const GroupTitle = styled.label`
	${groupTitle}
`

/** A group's title over what no one label can name — the day calendar, the time picker. */
export const GroupHeading = styled.h2`
	${groupTitle}
`

/** The note after a title — "(optional)", "(guessed from the place)". */
export const GroupMarker = styled.span`
	margin-left: 6px;
	${text.caption}
	font-weight: 400;
	color: ${textMuted};
`

export const GroupHint = styled.p`
	margin: 4px 0 0;
	${text.bodyXs}
	color: ${textMuted};
`

export const GroupBody = styled.div`
	margin-top: 12px;
	min-width: 0;
`

/** The itinerary's hairline, between the Extras sections. */
export const Divider = styled.hr`
	margin: 0;
	border: none;
	height: 1px;
	background: ${divider};
`

/* ─── What: the place ─── */

export const PlaceSearchRow = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
`

/** A picked place, as a field holding it: its tile, name and address, then the ways to change or clear it. */
export const PlaceCard = styled.div`
	${fieldStyles}
	display: flex;
	align-items: center;
	gap: 12px;
	height: auto;
	min-height: 44px;
	padding: 8px 6px 8px 10px;
`

export const PlaceTile = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 40px;
	height: 40px;
	border-radius: ${radii.small};
	background: ${warmTint};
	color: ${darkOrange};
`

export const PlaceText = styled.div`
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 2px;
`

const oneLine = css`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

export const PlaceName = styled.span`
	${text.bodySm}
	color: ${black};
	${oneLine}
`

export const PlaceAddress = styled.span`
	${text.bodyXs}
	color: ${textMuted};
	${oneLine}
`

/* ─── When: the day calendar ─── */

/**
 * The When step, in the create-trip When step's centred phone-width column:
 * the calendar, its day line and the time picker share one width, rather than
 * a narrow calendar over a full-width field.
 */
export const WhenStepForm = styled(StepForm)`
	max-width: 440px;
	margin: 0 auto;
`

/** The create-trip calendar's card, flush under its heading. */
export const DayCalendarCard = styled(CalendarCard)`
	margin-top: 0;
`

/**
 * A trip day on the create-trip calendar's cell. The picked day drops its
 * square tint for a circle of its destination's colour at full strength: among
 * the tinted squares, the one round cell reads as picked.
 */
export const CalendarDay = styled(DayCell)<{ $selected: boolean }>`
	${({ $selected, $fill }) =>
		$selected &&
		css`
			color: ${white};
			font-weight: 700;

			&,
			&:hover {
				background: radial-gradient(
					circle closest-side,
					${$fill === null || $fill === 'uncovered' ? textMuted : rangeAccent($fill)} 92%,
					transparent 96%
				);
			}
		`}
`

/** A day of the month outside the trip: a bare grey number, not a control, as the itinerary calendar draws it. */
export const CalendarOutsideDay = styled(BlankCell)`
	display: flex;
	align-items: center;
	justify-content: center;
	${text.bodyXs}
	color: ${darkGrey};
`

export const DayLine = styled.p`
	margin: 4px 0 0;
	${text.bodyXs}
	color: ${textMuted};
	${oneLine}
`

/* ─── Extras: optional sections ─── */

export const SectionHead = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`

export const SectionText = styled.div`
	flex: 1;
	min-width: 0;
`

export const SectionTitle = styled.h2`
	margin: 0;
	${text.title}
	color: ${black};
`

/**
 * The section's plus, in the accent: it fades once the section is open
 * (keeping its slot), or turns into the ✕ that closes it, quiet like the
 * flow's other ✕s.
 */
export const SectionToggle = styled(Button).attrs({ variant: 'icon' as const })<{ $hidden: boolean; $closes: boolean }>`
	flex-shrink: 0;
	visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
	${({ $closes }) =>
		!$closes &&
		css`
			color: ${darkOrange};
		`}
`

export const SectionBody = styled.div`
	margin-top: 14px;
	display: flex;
	flex-direction: column;
	gap: 12px;
`

export const AddRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
`

export const LinkRows = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`

/** The form kit's "Add Link", closing its section rather than leading into another field. */
export const AddLink = styled(AddLinkButton)`
	margin-bottom: 0;
`
